import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { Column, Pagination, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import { TabToggle, type TabItem } from '@shared/components/TabToggle';
import { Tooltip } from '@shared/components/Tooltip';
import { Menu, MenuSize, MenuVariant } from '@shared/components/Menu';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import myRequestsEmptyAnimation from '@/assets/lottie/noDataAnimation.json';
import { CERTIFICATE_LABELS } from '../../constants/labels';
import type { CertificateTemplateListItem } from '../../types';
import { useCertificateTemplateList } from './useCertificateTemplateList';

type ActiveTab = 'szablony' | 'certyfikaty';

const TABS: TabItem<ActiveTab>[] = [
  { id: 'szablony', label: CERTIFICATE_LABELS.TAB_TEMPLATES },
  { id: 'certyfikaty', label: CERTIFICATE_LABELS.TAB_CERTIFICATES },
];

const MIN_SPACE_BELOW_FOR_MENU = 70;

const formatCreatedAt = (iso: string | null | undefined): string => {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')}, ${get('hour')}:${get('minute')}:${get('second')}`;
};

const DotsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface TemplatesTabProps {
  onCreateClick: () => void;
}

function TemplatesTab({ onCreateClick }: TemplatesTabProps) {
  const navigate = useNavigate();
  const {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
  } = useCertificateTemplateList();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);

  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleApplyAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = useCallback(() => {
    setDateRange(appliedFilters.dateRange);
    setIsFilterOpen(false);
  }, [appliedFilters.dateRange, setDateRange]);

  useEffect(() => {
    if (!isFilterOpen) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isFilterOpen, handleCloseFilter]);

  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openMenuRowId]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    if ((appliedFilters.dateRange.startDate ?? '').trim()) {
      count += 1;
    }
    if ((appliedFilters.dateRange.endDate ?? '').trim()) {
      count += 1;
    }
    return count;
  }, [appliedFilters.dateRange]);

  const columns: Column<CertificateTemplateListItem>[] = useMemo(
    () => [
      {
        key: 'certTemplateName',
        header: CERTIFICATE_LABELS.LIST_COL_CERTIFICATE_NAME,
        minWidth: '360px',
        maxWidth: '380px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.certTemplateName} align="center">
              <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.certTemplateName}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'certTemplateId',
        header: CERTIFICATE_LABELS.LIST_COL_TEMPLATE_ID,
        minWidth: '200px',
        maxWidth: '240px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.certTemplateId}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.certTemplateId}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'formTemplateName',
        header: CERTIFICATE_LABELS.LIST_COL_TASK_FORM_NAME,
        minWidth: '380px',
        maxWidth: '400px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.formTemplateName} align="center">
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.formTemplateName}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'formTemplateId',
        header: CERTIFICATE_LABELS.LIST_COL_TASK_FORM_ID,
        minWidth: '200px',
        maxWidth: '240px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.formTemplateId}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.formTemplateId}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: CERTIFICATE_LABELS.LIST_COL_CREATED_AT,
        minWidth: '200px',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap block">
            {formatCreatedAt(row.createdAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: CERTIFICATE_LABELS.LIST_COL_ACTIONS,
        width: '80px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        centerHeader: true,
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    setMenuOpensUpward(
                      visibleBottom - buttonRect.bottom <
                        MIN_SPACE_BELOW_FOR_MENU
                    );
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                aria-label={`${CERTIFICATE_LABELS.LIST_COL_ACTIONS} ${row.certTemplateName}`}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                data-testid={`action-menu-button-${index ?? 0}`}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
              >
                <DotsIcon />
              </button>
              {isOpen && (
                <div
                  className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                >
                  <Menu
                    items={[
                      {
                        id: 'VIEW',
                        label: CERTIFICATE_LABELS.LIST_ACTION_VIEW,
                      },
                      {
                        id: 'EDIT',
                        label: CERTIFICATE_LABELS.LIST_ACTION_EDIT,
                      },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(`/Certyfikaty/Podgląd-szablonu/${row.id}`);
                      } else if (itemId === 'EDIT') {
                        navigate(`/Certyfikaty/Edycja-szablonu/${row.id}`, {
                          state: { returnTo: '/Certyfikaty' },
                        });
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowId, menuOpensUpward, navigate]
  );

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasSearch = search.trim() !== '';
  const hasDateFilter =
    (appliedFilters.dateRange.startDate ?? '').trim() !== '' ||
    (appliedFilters.dateRange.endDate ?? '').trim() !== '';

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    items.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !hasSearch &&
    !hasDateFilter;

  const showNoResults =
    !loading &&
    !isSearchClearing &&
    !hasNoData &&
    items.length === 0 &&
    (hasSearch || hasDateFilter);

  const noResultsDescription =
    hasSearch && hasDateFilter
      ? CERTIFICATE_LABELS.NO_RESULTS_DESCRIPTION_BOTH
      : hasSearch
        ? CERTIFICATE_LABELS.NO_RESULTS_DESCRIPTION_SEARCH
        : CERTIFICATE_LABELS.NO_RESULTS_DESCRIPTION_FILTER;

  return (
    <>
      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={CERTIFICATE_LABELS.LIST_SEARCH_PLACEHOLDER}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCreateClick}
              data-testid="create-certificate-template-toolbar-button"
              className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap">
                {CERTIFICATE_LABELS.BUTTON_CREATE_TEMPLATE}
              </span>
            </button>
            <div
              ref={filterContainerRef}
              className={`dropdown ${isFilterOpen ? 'show' : ''}`}
            >
              <FilterBar
                onClick={() => setIsFilterOpen((prev) => !prev)}
                isOpen={isFilterOpen}
                filterCount={appliedFilterCount}
              />
              <FilterPopup
                isOpen={isFilterOpen}
                onClose={handleCloseFilter}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                showDateRange
                onApply={handleApplyAndClose}
                onClear={handleClearFilters}
              />
            </div>
          </div>
        </div>
      )}

      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
      >
        {hasNoData ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-6 pb-16">
            <LottieAnimation
              animationData={myRequestsEmptyAnimation}
              loop
              width={300}
              aspectRatio="1 / 1"
            />
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-base font-medium leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.EMPTY_STATE_TITLE}
              </p>
              <p className="text-base font-normal leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.EMPTY_STATE_DESCRIPTION}
              </p>
            </div>
            <button
              type="button"
              onClick={onCreateClick}
              data-testid="create-certificate-template-button"
              className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                {CERTIFICATE_LABELS.BUTTON_CREATE_TEMPLATE}
              </span>
            </button>
          </div>
        ) : showNoResults ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-3">
            <LottieAnimation
              animationData={noResultsAnimation}
              width="clamp(160px, 18vw, 360px)"
              aspectRatio="432 / 350"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                {CERTIFICATE_LABELS.NO_RESULTS_TITLE}
              </p>
              <p className="text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                {noResultsDescription}
              </p>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={items}
            loading={loading || isSearchClearing}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            getStickyCellZIndex={(row, _idx, columnKey) =>
              columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
            }
            tableBodyStyle={{ gap: 4 }}
          />
        )}
      </div>

      {!hasNoData && totalCount > 10 && (
        <div className="shrink-0 px-4 -mx-4">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <p className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </p>
          )}
        </div>
      )}
    </>
  );
}

function CertificatesTab() {
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center w-full gap-6 pb-16">
      <LottieAnimation
        animationData={myRequestsEmptyAnimation}
        loop
        width={300}
        aspectRatio="1 / 1"
      />
      <div className="flex flex-col items-center gap-1 text-center">
        <p className="text-base font-medium leading-6 tracking-[0.5px] text-[#474A4C]">
          {CERTIFICATE_LABELS.CERTIFICATES_TAB_EMPTY_TITLE}
        </p>
        <p className="text-base font-normal leading-6 tracking-[0.5px] text-[#474A4C]">
          {CERTIFICATE_LABELS.CERTIFICATES_TAB_EMPTY_DESCRIPTION}
        </p>
      </div>
    </div>
  );
}

export default function CertificateList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tabParam = searchParams.get('tab');
  const activeTab: ActiveTab =
    tabParam === 'certyfikaty' ? 'certyfikaty' : 'szablony';

  const handleTabChange = (tab: ActiveTab) => {
    const next = new URLSearchParams();
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const handleCreateClick = useCallback(() => {
    navigate('/Certyfikaty/Utwórz-szablon-certyfikatu');
  }, [navigate]);

  return (
    <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
      <div className="flex items-center justify-between shrink-0 py-2.5">
        <div className="text-[#181A1B] text-[22px] font-normal leading-7">
          {CERTIFICATE_LABELS.PAGE_TITLE}
        </div>
      </div>

      <div className="shrink-0">
        <TabToggle
          tabs={TABS}
          activeTab={activeTab}
          onChange={handleTabChange}
          ariaLabel={CERTIFICATE_LABELS.PAGE_TITLE}
        />
      </div>

      {activeTab === 'szablony' ? (
        <TemplatesTab onCreateClick={handleCreateClick} />
      ) : (
        <CertificatesTab />
      )}
    </div>
  );
}
